import dayjs from 'dayjs';
import {
  PlanSectionType,
  NinjaSubscriptionPlan,
  WelcomeUpsellModalType,
  SubscriptionPlanInfo,
  CanonicalInterval,
  PlanKind,
  PlanFamily,
} from 'src/types';
import { UserTier } from 'src/types/models/UserTier';
import { tierLevels, PLAN_SELECTION_KINDS_MAP } from 'src/constants';

/**
 * @param amount in cents
 * @param discount in dollars
 * @returns price with discount
 */
export const getNewAmountWithDiscount = (amount: number, discount: number) => {
  return amount / 100 - discount;
};

export const getCalculatedSale = (amount: number, discount: number) => {
  return Math.ceil((discount * 100) / (amount / 100));
};

export const getNextUpsellModalTimestamp = (delay: number) => {
  return dayjs().valueOf() + delay;
};

/**
 * @deprecated
 */
export const getUpsellModalType = (
  userTier?: UserTier | null,
): WelcomeUpsellModalType | null => {
  // if (userTier === UserTier.OPEN) {
  //   return 'open-tier-upsell-modal';
  // }
  // if (userTier === UserTier.FREE) {
  //   return 'free-tier-upsell-modal';
  // }
  // if (userTier === UserTier.PRO_TRIAL) {
  //   return 'pro-trial-upsell-modal';
  // }

  return null;
};

export const defineIsUpgrade = (
  currentTier: UserTier,
  newTier: NinjaSubscriptionPlan,
) => {
  return tierLevels.indexOf(newTier) >= tierLevels.indexOf(currentTier);
};

export const isAppleOrAmpSource = (source?: string) => {
  return (
    source === 'apple' || source === 'amp' || source === 'google_play_store'
  );
};

export const getNextBillingCycleDate = (date?: string | null) => {
  return `, starting next billing cycle${
    !!date ? ` - ${dayjs(date).format('MMM D, YYYY')}` : ''
  }`;
};

/**
 * @deprecated
 */
export const selectSubscriptionPlan = (
  period: CanonicalInterval,
  type: PlanSectionType,
  plans?: SubscriptionPlanInfo[],
) => {
  // const planKinds = PLAN_SELECTION_KINDS_MAP[type];
  // if (planKinds) {
  //   return plans?.find(
  //     (plan) =>
  //       plan.period === period &&
  //       planKinds.includes(plan.plan_kind as PlanKind),
  //   );
  // }

  return plans?.find(
    (plan) => plan.period === period && plan.plan_tier === type.toLowerCase(),
  );
};

/**
 * @deprecated
 */
export const getPlanDisplayName = (planFamily: PlanFamily) => {
  if (planFamily === PlanFamily.MAX) {
    return 'Max';
  }

  if (planFamily === PlanFamily.BUSINESS) {
    return 'Business';
  }

  return 'Pro';
};

export const roundAmount = (value?: number) => {
  if (value !== 0 && !value) {
    return value;
  }
  return Math.round(value / 100);
};

export const getOriginalPriceLabel = ({
  displayedPlan,
}: {
  displayedPlan?: SubscriptionPlanInfo;
}) => {
  if (!displayedPlan) {
    return '-';
  }

  const price =
    displayedPlan.plan_group === 'teams'
      ? displayedPlan.per_seat.original_price
      : displayedPlan.original_price;

  const isMonthlyPeriod = displayedPlan.period === CanonicalInterval.MONTHLY;

  if (!price) {
    return '-';
  }

  return roundAmount(isMonthlyPeriod ? price : price / 12) || '-';
};

export const getDiscountedPriceLabel = ({
  displayedPlan,
}: {
  displayedPlan?: SubscriptionPlanInfo;
}) => {
  if (!displayedPlan) {
    return '-';
  }

  const price =
    displayedPlan.plan_group === 'teams'
      ? displayedPlan.per_seat.total_recurring_amount
      : displayedPlan.total_recurring_amount;

  const isMonthlyPeriod = displayedPlan.period === CanonicalInterval.MONTHLY;

  if (!price) {
    return '-';
  }
  return roundAmount(isMonthlyPeriod ? price : price / 12) || '-';
};

export const getTotalRecurringPriceLabel = ({
  displayedPlan,
}: {
  displayedPlan?: SubscriptionPlanInfo;
}) => {
  if (!displayedPlan) {
    return '-';
  }

  const price =
    displayedPlan.plan_group === 'teams'
      ? displayedPlan.per_seat.total_recurring_amount
      : displayedPlan.total_recurring_amount;

  const isMonthlyPeriod = displayedPlan.period === CanonicalInterval.MONTHLY;

  if (!price) {
    return '-';
  }

  return roundAmount(isMonthlyPeriod ? price : price / 12) || '-';
};

export const getFinalPriceLabel = ({
  displayedPlan,
}: {
  displayedPlan?: SubscriptionPlanInfo;
}) => {
  if (!displayedPlan) {
    return '-';
  }

  return getTotalRecurringPriceLabel({ displayedPlan });
};

export const getCorrespondingPlanKind = ({
  currentPlan,
  planFamily,
  targetInterval,
}: {
  currentPlan: PlanKind;
  planFamily: PlanFamily;
  targetInterval: CanonicalInterval;
}): PlanKind => {
  if (planFamily === PlanFamily.UNDEFINED || planFamily === PlanFamily.CREDIT) {
    return currentPlan;
  }

  const kindsMap = PLAN_SELECTION_KINDS_MAP[planFamily];

  if (
    !kindsMap ||
    (targetInterval !== CanonicalInterval.MONTHLY &&
      targetInterval !== CanonicalInterval.ANNUALLY)
  ) {
    return currentPlan;
  }

  const otherInterval =
    targetInterval === CanonicalInterval.MONTHLY
      ? CanonicalInterval.ANNUALLY
      : CanonicalInterval.MONTHLY;

  const currentIndex = kindsMap[otherInterval]?.findIndex(
    (plan) => plan === currentPlan,
  );

  const fallbackIndex = currentIndex !== -1 ? currentIndex : 0;

  return kindsMap[targetInterval][fallbackIndex] ?? currentPlan;
};

export const isSuperNinjaPlan = (planFamily?: PlanFamily) => {
  return planFamily === PlanFamily.MAX;
};
